import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  ViewChild,
  computed,
  inject,
  input,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { InfoCarousel, InfoItem } from 'www-cms';
import { BaseBuildingBlockComponent } from '../components/base-building-block.component';
import { LastItemBgClass } from '../../shell/shell.service';
import { ImageWithLoaderComponent } from '../components/image-with-loader/image-with-loader.component';
import { SideBySideImageTextComponent } from '../sideBySideImageText/side-by-side-image-text.component';
import { slideRound, slideRoundHidden } from './info-carousel.animations';
import { BreakpointObserver } from '@angular/cdk/layout';
import { BehaviorSubject, combineLatest, map } from 'rxjs';
import { FaqsComponent } from '../faqs/faqs.component';
import { ActivatedRoute, Router } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { getInfoCarouselId } from '../../shared/helpers/info-carousel-id';
import { DirectusDirective } from 'directus';

@Component({
  selector: 'ss-www-info-carousel',
  imports: [
    CommonModule,
    ImageWithLoaderComponent,
    SideBySideImageTextComponent,
    FaqsComponent,
    DirectusDirective,
  ],
  templateUrl: './info-carousel.component.html',
  styles: [
    `
      .split-color {
        background: linear-gradient(180deg, #000000 96%, #1c1c1c 4%);
        @media (max-width: 1024px) {
          background: linear-gradient(180deg, #000000 94%, #1c1c1c 6%);
        }
      }
      .split-color-light {
        background: linear-gradient(180deg, #000000 96%, #ffffff 4%);
        @media (max-width: 1024px) {
          background: linear-gradient(180deg, #000000 94%, #ffffff 6%);
        }
      }
      .split-color-no-icon {
        background: linear-gradient(180deg, #000000 96%, #1c1c1c 4%);
        @media (max-width: 1024px) {
          background: linear-gradient(180deg, #000000 94%, #1c1c1c 6%);
        }
      }
      .split-color-no-icon-light {
        background: linear-gradient(180deg, #000000 92%, #ffffff 8%);
        @media (max-width: 1024px) {
          background: linear-gradient(180deg, #000000 94%, #ffffff 6%);
        }
      }
      .gold-gradient {
        background: linear-gradient(113deg, #cf9c51 0%, #836231 100%);
      }
      .active-slide {
        background: linear-gradient(113deg, #cf9c51 0%, #836231 100%);
        background-clip: text;
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
      }
    `,
  ],
  animations: [slideRound, slideRoundHidden],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InfoCarouselComponent extends BaseBuildingBlockComponent {
  infoCarouselData = input.required<InfoCarousel, InfoCarousel>({
    transform: this.forceNoTopPadding,
  });
  private _scrollerHasOverflow = new BehaviorSubject(false);
  @ViewChild('slider') set slider(slider: ElementRef) {
    if (slider) {
      this._scrollerHasOverflow.next(
        slider.nativeElement.scrollWidth > slider.nativeElement.clientWidth
      );
    }
  }
  private activatedRoute = inject(ActivatedRoute);
  private queryParamMap = toSignal(this.activatedRoute.queryParamMap);
  activeIndex = computed(() => {
    if (!this.infoCarouselData() || !this.queryParamMap()) return 0;
    if (
      !this.queryParamMap()!.has(getInfoCarouselId(this.infoCarouselData().id))
    ) {
      return 0;
    }
    return +this.queryParamMap()!.get(
      getInfoCarouselId(this.infoCarouselData().id)
    )!;
  });
  private router = inject(Router);
  toLeft = false;
  private isSmallScreen$ = inject(BreakpointObserver)
    .observe(['(max-width: 1024px)'])
    .pipe(map((res) => res.breakpoints['(max-width: 1024px)']));

  showScrollForMore = toSignal(
    combineLatest([this.isSmallScreen$, this._scrollerHasOverflow]).pipe(
      map(
        ([isSmallScreen, scrollerHasOverflow]) =>
          isSmallScreen && scrollerHasOverflow
      )
    )
  );

  override getBgClass(): LastItemBgClass {
    return this.infoCarouselData().infoItems[0].faqsBlock?.darkBackground ===
      false
      ? 'bg-white'
      : 'bg-ss-www-background';
  }

  setActiveIndex(index: number): void {
    this.toLeft = this.isCarouselGoingLeft(index);
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: {
        [getInfoCarouselId(this.infoCarouselData().id)]: index,
      },
      queryParamsHandling: 'merge',
      state: { ignoreScroll: true },
    });
  }

  trackInfoItems(_: number, item: InfoItem): number {
    return item.id;
  }

  isCarouselGoingLeft(index: number): boolean {
    return index < this.activeIndex();
  }

  private forceNoTopPadding(infoCarousel: InfoCarousel): InfoCarousel {
    return {
      ...infoCarousel,
      infoItems: infoCarousel.infoItems.map((infoItem) => ({
        ...infoItem,
        infoBlock: infoItem.infoBlock
          ? {
              ...infoItem.infoBlock,
              topPadding: false,
            }
          : undefined,
      })),
    };
  }

  getActiveItemIsDark(activeIndex: number): boolean {
    return (
      this.infoCarouselData().infoItems[activeIndex]?.infoBlock?.invert ===
        false ||
      this.infoCarouselData().infoItems[activeIndex]?.faqsBlock
        ?.darkBackground === true
    );
  }
}
